import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Close, Send } from '@mui/icons-material';
import ApiService from '../../../config/api';
import Sidebar from '../components/Sidebar';
import AdminNavbar from '../components/AdminNavbar';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

const CreateEditFounder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
  });

  const [preview, setPreview] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  // ✅ Remove image
  const handleRemoveImage = () => {
    setFormData({ ...formData, image: null });
    setPreview(null);
  };

  // ✅ Fetch founder details if edit mode
  const fetchFounder = async () => {
    try {
      const res = await ApiService.get(`admin/founder/${id}`);
      if (!res.data.error) {
        setFormData({
          name: res.data.meta.name || '',
          description: res.data.meta.description || '',
          image: null,
        });
        if (res.data.meta.image) {
          setPreview(res.data.meta.image);
        }
      }
    } catch (err) {
      console.error('Error fetching founder:', err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchFounder();
    }
  }, [id]);

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      if (formData.image) {
        data.append('image', formData.image); // file upload
      }

      if (id) {
        // Update founder
        await ApiService.put(`admin/edit/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Founder updated successfully!');
      } else {
        // Create new founder
        await ApiService.post('admin/create-founder', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Founder created successfully!');
      }

      navigate('/admin/founders');
    } catch (err) {
      console.error('Error saving founder:', err);
      toast.error('Failed to save founder. Please try again.');
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: 'var(--bs-secondary)',
        minHeight: '100vh',
      }}
    >
      <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />
      <Box style={{ backgroundColor: 'var(--bs-secondary)' }}>
        <AdminNavbar />
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            color: "var(--bs-primary)",
            textTransform: "none",
            fontWeight: 500,
            padding: 2
          }}
        >
          Back to Founders
        </Button>
        <Box
          maxWidth="600px"
          mx="auto"
          p={2}
          borderRadius="16px"
          boxShadow={3}
          bgcolor="var(--bs-primary)"
        >
          <Typography
            variant="h5"
            sx={{ color: 'var(--bs-accent)', textAlign: 'center' }}
          >
            {id ? 'Edit Founder' : 'Add Founder'}
          </Typography>

          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              focused
              InputProps={{
                sx: {
                  color: 'var(--bs-accent)', // text inside input
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'var(--bs-accent)', // label color
                  '&.Mui-focused': {
                    color: 'var(--bs-accent)',
                  },
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                },
              }}
            />


            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={6}
              required focused
              InputProps={{
                sx: {
                  color: 'var(--bs-accent)', // text inside input
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'var(--bs-accent)', // label color
                  '&.Mui-focused': {
                    color: 'var(--bs-accent)',
                  },
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--bs-accent)',
                  },
                },
              }} />

            <p
              style={{
                color: "#d1d1d1",
                fontSize: "12px",
                marginTop: "4px"
              }}
            >
              * Please upload an image with dimensions 612px × 408px
            </p>

            <Box>
              <Button
                variant="outlined"
                component="label"
                sx={{ color: 'var(--bs-accent)', borderColor: 'var(--bs-accent)' }}
              >
                Upload Image
                <input type="file" hidden accept="image/*" onChange={handleFileChange} />
              </Button>
            </Box>

            {/* Image Preview with Remove */}
            {preview && (
              <Box mt={2} position="relative" display="inline-block">
                <img
                  src={preview}
                  alt="Preview"
                  style={{ width: '120px', height: '120px', borderRadius: '8px', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'red' },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Box mt={2} display="flex" justifyContent="space-between">
              <Button
                variant="outlined"
                sx={{ color: 'var(--bs-accent)', borderColor: 'var(--bs-accent)' }}
                onClick={() => navigate('/admin/founders')}
              >
                Cancel
              </Button>

              <Button
                variant="contained" endIcon={<Send />}

                type="submit"
                sx={{
                  backgroundColor: 'var(--bs-accent)',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'var(--bs-secondary)', color: 'var(--bs-primary)', },
                }}
              >
                {id ? 'Update' : 'Create'}
              </Button>


            </Box>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateEditFounder;
